import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Minus, RotateCcw, Search, ShieldCheck, X } from "lucide-react";
import { Avatar } from "@/components/Avatar";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useChat } from "@/lib/chat";
import { api, errMsg } from "@/lib/api";
import { analytics } from "@/lib/analytics";

const ROLE_HELP = { admin: "Admins have every permission and are the only ones who can change roles, permissions and mobile access.", member: "Default for everyone invited to the workspace.", guest: "Limited access — DMs, reactions, files and joining huddles. Good for contractors.", bot: "Programs with a token (Admin › Bots). Post, reply, react, upload and DM — no channel creation, huddles or @channel unless you allow it." };

// Clicking always flips the effective value; an override equal to the role default collapses back to "default".
const nextState = (on, roleDefault) => (!on === roleDefault ? null : !on);

const Cell = ({ perm, row, onChange, busy }) => {
  const override = row.overrides[perm.key];
  const roleDefault = row.roleDefaults?.[perm.key] ?? false;
  const on = row.is_admin ? true : row.effective[perm.key];
  const state = row.is_admin ? "admin" : override == null ? "default" : override ? "allow" : "deny";
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button className={`tc-permcell is-${on ? "on" : "off"} is-${state}`} disabled={row.is_admin || busy} onClick={() => onChange(row, perm.key, nextState(on, roleDefault))}
            data-testid={`perm-${row.handle}-${perm.key}`} data-state={state} aria-label={`${perm.label}: ${on ? "allowed" : "denied"}`}>
            {on ? <Check size={14} /> : <X size={14} />}
            {state !== "default" && state !== "admin" && <i className="tc-permcell__dot" />}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="tc-tooltip">
          <b>{perm.label}</b>
          <div style={{ opacity: 0.8, maxWidth: 260 }}>{perm.description}</div>
          <div style={{ marginTop: 4 }}>{row.is_admin ? "Admins always have this." : `${row.roleLabel} default: ${roleDefault ? "On" : "Off"}${override == null ? "" : ` · Override: ${override ? "Allowed" : "Denied"}`}`}</div>
          {!row.is_admin && <div style={{ opacity: 0.7 }}>Click to {on ? "deny" : "allow"}{!on === roleDefault ? " (back to role default)" : ""}</div>}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const RoleDefaults = ({ catalog }) => {
  const [open, setOpen] = useState(false);
  const roles = Object.entries(catalog.roles);
  return (
    <div className="tc-admin-card" data-testid="role-defaults-card">
      <button className="tc-admin-card__head" style={{ width: "100%", textAlign: "left", cursor: "pointer", background: "none", border: 0, borderBottom: open ? undefined : 0 }} onClick={() => setOpen((o) => !o)} data-testid="role-defaults-toggle">
        <ShieldCheck size={16} /> Role defaults <span className="right">{open ? "Hide" : "Show"}</span>
      </button>
      {open && (
        <div style={{ overflowX: "auto" }}>
          <table className="tc-table" data-testid="role-defaults-table">
            <thead><tr><th>Permission</th><th>Admin</th>{roles.map(([r, d]) => <th key={r}>{d.label}</th>)}</tr></thead>
            <tbody>
              {catalog.permissions.map((p) => (
                <tr key={p.key}><td><b>{p.label}</b><div className="muted" style={{ fontSize: 12 }}>{p.description}</div></td><td><span className="tc-permcell is-on is-admin"><Check size={14} /></span></td>
                  {roles.map(([r, d]) => <td key={r}><span className={`tc-permcell is-${d.defaults[p.key] ? "on" : "off"} is-default`}>{d.defaults[p.key] ? <Check size={14} /> : <X size={14} />}</span></td>)}</tr>
              ))}
              {catalog.admin_only.map((p) => (
                <tr key={p.key} className="is-dim"><td><b>{p.label}</b><div className="muted" style={{ fontSize: 12 }}>Admins only — can't be granted to members or guests.</div></td><td><span className="tc-permcell is-on is-admin"><Check size={14} /></span></td>
                  {roles.map(([r]) => <td key={r}><span className="tc-permcell is-off is-locked"><Minus size={14} /></span></td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const PermissionsMatrix = () => {
  const { meId } = useChat();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [onlyOverrides, setOnlyOverrides] = useState(false);
  const [busy, setBusy] = useState("");
  const focus = params.get("user");
  const load = () => api.get("/admin/permissions").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);
  const rows = useMemo(() => {
    if (!data) return [];
    return data.users.map((u) => ({ ...u, roleLabel: data.catalog.roles[u.role]?.label || "Admin", roleDefaults: data.catalog.roles[u.role]?.defaults }))
      .filter((u) => !q || `${u.display_name} ${u.handle} ${u.email}`.toLowerCase().includes(q.toLowerCase()))
      .filter((u) => !onlyOverrides || Object.keys(u.overrides).length)
      .filter((u) => !focus || u.id === focus);
  }, [data, q, onlyOverrides, focus]);
  const groups = useMemo(() => {
    if (!data) return [];
    const g = [];
    data.catalog.permissions.forEach((p) => { const last = g[g.length - 1]; if (last && last.name === p.group) last.items.push(p); else g.push({ name: p.group, items: [p] }); });
    return g;
  }, [data]);
  const patch = async (row, body, label) => {
    setBusy(row.id);
    try {
      const { data: updated } = await api.patch(`/admin/users/${row.id}`, body);
      setData((d) => ({ ...d, users: d.users.map((u) => (u.id === row.id ? { ...u, ...updated } : u)) }));
      analytics.track("admin_permission_changed", { category: "admin", target: "user", target_id: row.id, label, metadata: body });
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const onCell = (row, key, value) => patch(row, { permissions: { [key]: value } }, key);
  const onRole = (row, role) => patch(row, { role }, `role:${role}`).then(() => toast.success(`${row.display_name} is now a ${data.catalog.roles[role].label.toLowerCase()}`));
  const onReset = (row) => patch(row, { reset_permissions: true }, "reset").then(() => toast.success(`${row.display_name} reset to ${row.roleLabel.toLowerCase()} defaults`));
  if (!data) return <div data-testid="admin-permissions"><h1>Permissions</h1><p className="lead">Loading…</p></div>;
  const overridden = data.users.filter((u) => Object.keys(u.overrides).length).length;
  return (
    <div data-testid="admin-permissions">
      <h1>Permissions</h1>
      <p className="lead">Who can do what. Every member starts from their role's defaults; click any cell to allow or deny that one thing for that one person. Mobile access is managed on the Members page.</p>
      <RoleDefaults catalog={data.catalog} />
      <div className="tc-admin__toolbar">
        <div className="tc-searchbox"><Search size={14} /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a member…" data-testid="perm-search" /></div>
        <label className="tc-check" style={{ fontSize: 13 }}><input type="checkbox" checked={onlyOverrides} onChange={(e) => setOnlyOverrides(e.target.checked)} data-testid="perm-only-overrides" /> Only members with custom permissions ({overridden})</label>
        {focus && <a className="tc-btn tc-btn--ghost sm" href="/admin/permissions" data-testid="perm-clear-focus">Show everyone</a>}
        <span className="grow" />
        <span className="tc-permlegend"><span className="tc-permcell is-on is-default"><Check size={12} /></span> role default on <span className="tc-permcell is-off is-default"><X size={12} /></span> off <span className="tc-permcell is-on is-allow"><Check size={12} /><i className="tc-permcell__dot" /></span> allowed for this person <span className="tc-permcell is-off is-deny"><X size={12} /><i className="tc-permcell__dot" /></span> denied</span>
      </div>
      <div className="tc-admin-card">
        <div className="tc-permwrap" style={{ overflowX: "auto" }}>
          <table className="tc-table tc-table--sticky tc-permtable" data-testid="permissions-matrix">
            <thead>
              <tr><th rowSpan={2} style={{ minWidth: 240 }}>Member</th><th rowSpan={2} style={{ minWidth: 112 }}>Role</th>{groups.map((g) => <th key={g.name} colSpan={g.items.length} className="tc-permtable__group">{g.name}</th>)}<th rowSpan={2} />
              </tr>
              <tr>{data.catalog.permissions.map((p) => <th key={p.key} className="tc-permtable__col"><span>{p.label}</span></th>)}</tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} data-testid={`perm-row-${row.handle}`} className={row.is_active === false ? "is-dim" : ""}>
                  <td><div className="usercell"><Avatar user={row} size={24} radius={5} /><div><div>{row.display_name}{row.is_bot && <span className="tc-msg__app">Bot</span>}{row.id === meId && <span className="muted"> (you)</span>}</div><div className="sub mono">{row.email}</div></div></div></td>
                  <td>
                    {row.is_admin ? <span className="tc-pill api">Admin</span> : (
                      <select className="tc-select sm" value={row.role} onChange={(e) => onRole(row, e.target.value)} disabled={busy === row.id} data-testid={`perm-role-${row.handle}`}>
                        {Object.entries(data.catalog.roles).filter(([r]) => row.is_bot || r !== "bot").map(([r, d]) => <option key={r} value={r}>{d.label}</option>)}
                      </select>
                    )}
                  </td>
                  {data.catalog.permissions.map((p) => <td key={p.key} className="tc-permtable__cell"><Cell perm={p} row={row} onChange={onCell} busy={busy === row.id} /></td>)}
                  <td>{!row.is_admin && Object.keys(row.overrides).length > 0 && <button className="tc-iconbtn" title="Reset to role defaults" onClick={() => onReset(row)} data-testid={`perm-reset-${row.handle}`}><RotateCcw size={14} /></button>}</td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={data.catalog.permissions.length + 3} className="muted" style={{ textAlign: "center", padding: 24 }}>No members match.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      <div className="tc-admin-card" style={{ padding: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
          {Object.entries(ROLE_HELP).map(([r, t]) => <div key={r} data-testid={`role-help-${r}`}><b>{r === "admin" ? "Admin" : data.catalog.roles[r].label}</b><div className="muted" style={{ fontSize: 13, marginTop: 4 }}>{t}</div></div>)}
        </div>
      </div>
    </div>
  );
};

export default PermissionsMatrix;
